import { Operations, ProblemData } from "../../MathFactsGenerator/Constants.ts";
import { AnswerResult, checkAnswer } from "../Quiz/answer.ts";

/*
 * The fill-the-chart game, as pure functions (ADR 0054 §6). The component owns
 * storage, the DOM and focus; everything here is tested with plain values.
 */

export const CHART_SIZE = 10;

/**
 * The two charts (ADR 0098 §2). Subtraction and division are not offered: on a
 * chart their answer would sit in the header beside the question.
 */
export type ChartOperation = Operations.Addition | Operations.Multiplication;

export const CHART_OPERATIONS: ChartOperation[] = [Operations.Addition, Operations.Multiplication];

/** Anything but addition opens the multiplication chart, as the game always did. */
export function chartOperationFor(operation: unknown): ChartOperation {
    return operation === Operations.Addition ? Operations.Addition : Operations.Multiplication;
}

/** The sign a problem carries (`ProblemData.operator`): "x" as the worksheet prints it. */
function operatorOf(operation: ChartOperation): string {
    return operation === Operations.Addition ? "+" : "x";
}

/** What a fact on the chart comes to. */
export function chartAnswer(a: number, b: number, operation: ChartOperation = Operations.Multiplication): number {
    return operation === Operations.Addition ? a + b : a * b;
}

/** A fact as it is asked: `a` down the side, `b` across the top. */
export interface ChartFact {
    a: number;
    b: number;
}

export interface ChartProgress {
    /** Fact ids answered right; each fills both of its squares. */
    filled: string[];
    /** Fact ids answered wrong and not yet answered right since. */
    tricky: string[];
    /** Wrong answers given while filling this chart. */
    wrong: number;
}

/** Returns an index in [0, n). Injected so tests can choose. */
export type PickIndex = (n: number) => number;

export const EMPTY_PROGRESS: ChartProgress = { filled: [], tricky: [], wrong: 0 };

/**
 * Orientation-free: 7 x 8 and 8 x 7 are one fact, and so are 7 + 8 and 8 + 7
 * (§2). A multiplication id keeps its original "7x8" form, which is what an
 * earlier visit saved; an addition id is "7+8".
 */
export function factId(a: number, b: number, operation: ChartOperation = Operations.Multiplication): string {
    const sign = operation === Operations.Addition ? "+" : "x";
    return a <= b ? `${a}${sign}${b}` : `${b}${sign}${a}`;
}

export function allFactIds(size: number = CHART_SIZE, operation: ChartOperation = Operations.Multiplication): string[] {
    const ids: string[] = [];
    for (let a = 1; a <= size; a++) {
        for (let b = a; b <= size; b++) {
            ids.push(factId(a, b, operation));
        }
    }
    return ids;
}

export function totalFacts(size: number = CHART_SIZE): number {
    return (size * (size + 1)) / 2;
}

export function isComplete(progress: ChartProgress, size: number = CHART_SIZE): boolean {
    return progress.filled.length >= totalFacts(size);
}

function parseId(id: string): [number, number] {
    const [a, b] = id.split(/[x+]/).map(Number);
    return [a, b];
}

/**
 * The next fact to ask: a random empty square, never `avoidId` if anything
 * else is left, asked either way round at random (§2). Null once the chart is
 * full.
 */
export function pickNext(
    progress: ChartProgress,
    pick: PickIndex,
    avoidId: string | null = null,
    size: number = CHART_SIZE,
    operation: ChartOperation = Operations.Multiplication
): ChartFact | null {
    const filled = new Set(progress.filled);
    const empty = allFactIds(size, operation).filter((id) => !filled.has(id));
    if (empty.length === 0) {
        return null;
    }
    const candidates = empty.length > 1 ? empty.filter((id) => id !== avoidId) : empty;
    const [x, y] = parseId(candidates[pick(candidates.length)]);
    return pick(2) === 0 ? { a: x, b: y } : { a: y, b: x };
}

export function problemFor(fact: ChartFact, operation: ChartOperation = Operations.Multiplication): ProblemData {
    return {
        key: fact.a * 100 + fact.b,
        numbers: [fact.a, fact.b, chartAnswer(fact.a, fact.b, operation)],
        operator: operatorOf(operation),
    };
}

/**
 * Marks an answer through the quiz's own `checkAnswer`, so what counts as
 * right cannot drift from the quiz. An empty box is a skip and changes nothing.
 */
export function answerFact(
    progress: ChartProgress,
    fact: ChartFact,
    typed: string,
    operation: ChartOperation = Operations.Multiplication
): { progress: ChartProgress; result: AnswerResult } {
    const result = checkAnswer(problemFor(fact, operation), typed);
    const id = factId(fact.a, fact.b, operation);
    if (result === "correct") {
        return {
            result,
            progress: {
                ...progress,
                filled: progress.filled.includes(id) ? progress.filled : [...progress.filled, id],
                tricky: progress.tricky.filter((t) => t !== id),
            },
        };
    }
    if (result === "wrong") {
        return {
            result,
            progress: {
                ...progress,
                tricky: progress.tricky.includes(id) ? progress.tricky : [...progress.tricky, id],
                wrong: progress.wrong + 1,
            },
        };
    }
    return { result, progress };
}

/**
 * Keeps only what a stored value can honestly mean for this chart: known ids,
 * no duplicates, nothing both filled and tricky, a non-negative whole count.
 */
export function sanitizeProgress(
    value: unknown,
    size: number = CHART_SIZE,
    operation: ChartOperation = Operations.Multiplication
): ChartProgress {
    if (!value || typeof value !== "object") {
        return EMPTY_PROGRESS;
    }
    const raw = value as Record<string, unknown>;
    const valid = new Set(allFactIds(size, operation));
    const ids = (list: unknown): string[] =>
        Array.isArray(list)
            ? Array.from(new Set(list.filter((id): id is string => typeof id === "string" && valid.has(id))))
            : [];
    const filled = ids(raw.filled);
    const tricky = ids(raw.tricky).filter((id) => !filled.includes(id));
    const wrong =
        typeof raw.wrong === "number" && Number.isFinite(raw.wrong) && raw.wrong > 0
            ? Math.floor(raw.wrong)
            : 0;
    return { filled, tricky, wrong };
}
